import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Star, Award, Diamond, Check, ArrowRight, Loader2, Gift, Truck, Calendar, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { useRazorpay } from 'react-razorpay';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const tiers = [
  {
    id: 'free',
    name: 'Introductory',
    cardTitle: 'WELCOME CARD',
    subtitle: 'Welcome to Nistala\'s',
    price: 'Free',
    priceNum: 0,
    priceLabel: 'FREE TO JOIN',
    priceSubtext: 'Open to everyone',
    period: '',
    tagline: 'YOUR JOURNEY TO BETTER HEALTH BEGINS HERE.',
    cardBg: 'bg-gradient-to-br from-[#faf7f2] to-[#f0ebe3]',
    cardBorder: 'border-[#d4c5b0]',
    taglineBg: 'bg-[#3d5a28]',
    priceBg: 'bg-[#faf7f2] border-[#d4c5b0]',
    cta: 'Join Free',
    popular: false,
    benefits: [
      { icon: '🏷️', text: '10% OFF on first 3 orders' },
      { icon: '🎁', text: 'Free consultation / tasting' },
      { icon: '📋', text: 'Access to basic subscription plans' },
      { icon: '⭐', text: 'Earn points (Intro rate)' },
    ]
  },
  {
    id: 'silver',
    name: 'Silver',
    cardTitle: 'SILVER LOYALTY CARD',
    subtitle: 'For Our Valued Members',
    price: '999',
    priceNum: 999,
    priceLabel: 'ANNUAL MEMBERSHIP',
    priceSubtext: '₹999 / year',
    period: '/year',
    tagline: 'MORE VALUE. MORE CARE. JUST FOR YOU.',
    cardBg: 'bg-gradient-to-br from-[#e8e8e8] via-[#c0c0c0] to-[#a8a8a8]',
    cardBorder: 'border-[#b0b0b0]',
    taglineBg: 'bg-[#4a4a4a]',
    priceBg: 'bg-[#f0f0f0] border-[#c0c0c0]',
    cta: 'Get Silver',
    popular: false,
    benefits: [
      { icon: '🏷️', text: '10-12% OFF on all orders' },
      { icon: '🔑', text: 'Priority access to subscriptions' },
      { icon: '🎁', text: '1 free item / month (small value)' },
      { icon: '🎂', text: 'Birthday / Anniversary offer' },
      { icon: '⭐', text: 'Earn points faster than Intro tier' },
    ]
  },
  {
    id: 'gold',
    name: 'Gold',
    cardTitle: 'GOLD LOYALTY CARD',
    subtitle: 'For Our Loyal Community',
    price: '2,999',
    priceNum: 2999,
    priceLabel: 'ANNUAL MEMBERSHIP',
    priceSubtext: '₹2,999 / year',
    period: '/year',
    tagline: 'PREMIUM BENEFITS. EXCLUSIVE EXPERIENCES.',
    cardBg: 'bg-gradient-to-br from-[#f5d98a] via-[#d4a534] to-[#b8860b]',
    cardBorder: 'border-[#d4a534]',
    taglineBg: 'bg-[#8B6914]',
    priceBg: 'bg-[#fdf5dc] border-[#d4a534]',
    cta: 'Upgrade to Gold',
    popular: true,
    benefits: [
      { icon: '🏷️', text: '15-18% OFF on all orders' },
      { icon: '🚚', text: 'Free delivery / priority service' },
      { icon: '✨', text: 'Exclusive menu / early product access' },
      { icon: '📦', text: 'Monthly curated box or add-on' },
      { icon: '🎪', text: 'Invitations to workshops / events' },
    ]
  },
  {
    id: 'platinum',
    name: 'Platinum',
    cardTitle: 'PLATINUM LIFETIME CARD',
    subtitle: 'For Our Elite Circle',
    price: '25,000',
    priceNum: 25000,
    priceLabel: 'ONE TIME PAYMENT',
    priceSubtext: '₹25,000 onwards',
    period: ' one-time',
    tagline: 'A LIFETIME OF PRIVILEGES. A LIFETIME OF WELLNESS.',
    cardBg: 'bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#111111]',
    cardBorder: 'border-[#D4A574]/50',
    taglineBg: 'bg-[#111111]',
    priceBg: 'bg-[#1a1a1a] border-[#D4A574]/50',
    cta: 'Become Platinum Member',
    popular: false,
    benefits: [
      { icon: '🏷️', text: '20-25% OFF across all offerings' },
      { icon: '👤', text: 'Dedicated relationship manager' },
      { icon: '📦', text: 'Free monthly premium box' },
      { icon: '🏢', text: 'Access to franchise / investment' },
      { icon: '🎪', text: 'Private invites to tastings & retreats' },
      { icon: '🌟', text: 'Priority across ALL services' },
      { icon: '♾️', text: 'Lifetime validity' },
    ]
  }
];

const Membership = () => {
  const { Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(null);
  const [joinForm, setJoinForm] = useState({ name: '', email: '', phone: '' });
  const [showJoinForm, setShowJoinForm] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleJoin = async (tier) => {
    if (tier.priceNum === 0) {
      setShowJoinForm(tier.id);
      return;
    }
    setShowJoinForm(tier.id);
  };

  const processPayment = async (tier) => {
    if (!joinForm.name || !joinForm.email || !joinForm.phone) return;

    if (tier.priceNum === 0) {
      // Free tier - just submit
      setLoading(tier.id);
      try {
        await fetch(`${API}/api/membership/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...joinForm, tier: tier.id, amount: 0 })
        });
        setSuccess(true);
        setShowJoinForm(null);
        setTimeout(() => setSuccess(false), 4000);
      } catch {}
      setLoading(null);
      return;
    }

    // Paid tier - Razorpay
    setLoading(tier.id);
    try {
      const res = await fetch(`${API}/api/membership/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...joinForm, tier: tier.id })
      });
      if (!res.ok) throw new Error('Failed to create order');
      const data = await res.json();

      const options = {
        key: RAZORPAY_KEY,
        amount: data.amount,
        currency: 'INR',
        name: "Nistala's Group",
        description: `${tier.name} Membership`,
        order_id: data.razorpay_order_id,
        handler: async (response) => {
          try {
            await fetch(`${API}/api/membership/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                membership_id: data.membership_id
              })
            });
            setSuccess(true);
            setShowJoinForm(null);
            setTimeout(() => setSuccess(false), 4000);
          } catch {}
        },
        prefill: { name: joinForm.name, email: joinForm.email, contact: joinForm.phone },
        theme: { color: '#5C7A3A' },
        modal: { ondismiss: () => setLoading(null) }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', () => setLoading(null));
      rzp.open();
    } catch {}
    setLoading(null);
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="membership-page">
      <SEO title="Membership" description="Join Nistala's Circle — exclusive membership tiers with discounts, free delivery, curated boxes, and premium benefits." />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C1810] via-[#3d2418] to-[#1a0e08]" />
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 30% 50%, #D4A574 0%, transparent 50%), radial-gradient(circle at 70% 50%, #5C7A3A 0%, transparent 50%)' }} />
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-16 pb-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-[#D4A574]/15 border border-[#D4A574]/25 px-4 py-1.5 rounded-full mb-5">
              <Crown size={14} className="text-[#D4A574]" />
              <span className="text-xs tracking-[0.15em] uppercase text-[#D4A574] font-semibold">Loyalty Membership Program</span>
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Join the <span className="text-[#D4A574]">Nistala's Circle</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-2">
              More Benefits. More Value. More Belonging.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-sm text-white/50 max-w-lg mx-auto">
              Choose your benefits level and unlock exclusive discounts, priority access, curated boxes, and premium experiences.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Success Banner */}
      {success && (
        <div className="bg-[#5C7A3A] text-white text-center py-3 text-sm font-medium" data-testid="membership-success">
          <Check size={16} className="inline mr-2" /> Welcome to Nistala's Circle! Your membership is active.
        </div>
      )}

      {/* Tier Cards */}
      <section className="py-14 -mt-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((tier, idx) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="relative"
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-[#5C7A3A] text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">Most Popular</span>
                  </div>
                )}
                <div className="h-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden flex flex-col" data-testid={`tier-card-${tier.id}`}>

                  {/* Column Header */}
                  <div className="text-center px-4 pt-5 pb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#2C1810]">{idx + 1}. {tier.name} Card</p>
                    <p className="text-xs italic text-[#7a6555] mt-0.5">{tier.subtitle}</p>
                  </div>

                  {/* Physical Card Visual */}
                  <div className="px-4 pb-3">
                    <div className={`${tier.cardBg} ${tier.cardBorder} border rounded-xl p-4 text-center shadow-md relative overflow-hidden`}>
                      {/* Decorative leaf */}
                      <div className="absolute top-2 right-2 opacity-20 text-2xl">🌿</div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C7A3A] mb-1" style={tier.id === 'platinum' ? { color: '#D4A574' } : {}}>
                        Nistala's
                      </p>
                      <p className={`text-sm font-bold ${tier.id === 'platinum' ? 'text-[#D4A574]' : 'text-[#2C1810]'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                        {tier.cardTitle}
                      </p>
                      <p className={`text-[9px] italic mt-1 ${tier.id === 'platinum' ? 'text-[#D4A574]/60' : 'text-[#7a6555]'}`}>Where Tradition Evolves</p>
                    </div>
                  </div>

                  {/* Tagline Banner */}
                  <div className={`${tier.taglineBg} mx-4 px-3 py-2 rounded-lg mb-3`}>
                    <p className="text-[10px] font-bold text-white text-center tracking-wide leading-tight">{tier.tagline}</p>
                  </div>

                  {/* Benefits */}
                  <div className="px-4 flex-1">
                    <ul className="space-y-2">
                      {tier.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#2C1810]">
                          <span className="text-xs mt-0.5 flex-shrink-0">{b.icon}</span>
                          <span className="text-xs leading-relaxed">{b.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Pricing Footer */}
                  <div className="px-4 pt-4 pb-4 mt-auto">
                    <div className={`${tier.priceBg} border rounded-xl p-3 text-center mb-3`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${tier.id === 'platinum' ? 'text-[#D4A574]' : 'text-[#7a6555]'}`}>{tier.priceLabel}</p>
                      <p className={`text-sm font-bold ${tier.id === 'platinum' ? 'text-[#D4A574]' : 'text-[#2C1810]'}`}>{tier.priceSubtext}</p>
                    </div>

                    {/* CTA or Form */}
                    {showJoinForm === tier.id ? (
                      <div className="space-y-2" data-testid={`join-form-${tier.id}`}>
                        <Input placeholder="Full name" value={joinForm.name} onChange={e => setJoinForm(f => ({ ...f, name: e.target.value }))} required data-testid={`membership-name-${tier.id}`} className="text-xs h-9" />
                        <Input placeholder="Email" type="email" value={joinForm.email} onChange={e => setJoinForm(f => ({ ...f, email: e.target.value }))} required data-testid={`membership-email-${tier.id}`} className="text-xs h-9" />
                        <Input placeholder="Phone (10 digits)" type="tel" value={joinForm.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setJoinForm(f => ({ ...f, phone: v })); }} maxLength={10} required data-testid={`membership-phone-${tier.id}`} className="text-xs h-9" />
                        <Button
                          onClick={() => processPayment(tier)}
                          disabled={loading === tier.id || !joinForm.name || !joinForm.email || joinForm.phone.length !== 10}
                          className={`w-full font-semibold py-3 text-xs cursor-pointer ${tier.popular ? 'bg-[#5C7A3A] hover:bg-[#4a6530] text-white' : 'bg-[#2C1810] hover:bg-[#1a0e08] text-white'}`}
                          data-testid={`confirm-join-${tier.id}`}
                        >
                          {loading === tier.id ? <><Loader2 size={12} className="animate-spin mr-1" /> Processing...</> : tier.priceNum === 0 ? 'Join Now' : `Pay ₹${tier.price}`}
                        </Button>
                        <button onClick={() => setShowJoinForm(null)} className="w-full text-[10px] text-[#7a6555] hover:text-[#2C1810] cursor-pointer">Cancel</button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleJoin(tier)}
                        className={`w-full font-semibold py-3 text-xs cursor-pointer ${tier.popular ? 'bg-[#5C7A3A] hover:bg-[#4a6530] text-white shadow-md' : 'bg-[#2C1810] hover:bg-[#1a0e08] text-white'}`}
                        data-testid={`join-btn-${tier.id}`}
                      >
                        {tier.cta} {tier.priceNum > 0 && <ArrowRight size={12} className="ml-1" />}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] text-center mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Why Members <span className="text-[#5C7A3A]">Love Us</span>
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: Gift, title: 'Exclusive Savings', desc: 'Up to 25% off on every order' },
              { icon: Truck, title: 'Free Delivery', desc: 'Gold & Platinum members' },
              { icon: Calendar, title: 'Monthly Surprises', desc: 'Curated food boxes' },
              { icon: Sparkles, title: 'Premium Access', desc: 'Events, new items, priority' }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }} className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#5C7A3A]/10 flex items-center justify-center">
                  <item.icon size={22} className="text-[#5C7A3A]" />
                </div>
                <h4 className="text-sm font-bold text-[#2C1810] mb-1">{item.title}</h4>
                <p className="text-xs text-[#7a6555]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#2C1810]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Not sure which plan? <span className="text-[#D4A574]">Start Free.</span>
            </h2>
            <p className="text-sm text-white/60 mb-6">Join the Introductory tier at no cost. Upgrade anytime as you experience the benefits.</p>
            <Button onClick={() => { setShowJoinForm('free'); document.querySelector('[data-testid="tier-card-free"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg cursor-pointer" data-testid="cta-join-free">
              Join Free Today <ArrowRight size={16} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Membership;
